// src/pages/configuracion/UsuariosPage.tsx
import { useState } from 'react';
import {
  Card,
  Button,
  Input,
  Badge,
  Spinner,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { toast } from '@/components/ui/Toast';
import { Search, Edit2, Trash2, Key, UserPlus } from 'lucide-react';
import { useUsuarios, useEliminarUsuario, useResetearPassword } from '@/hooks/queries/useConfiguracion';
import { UsuarioFormModal } from './components/UsuarioFormModal';
import { formatDateTime } from '@/lib/utils';
import type { UsuarioAdmin } from '@/types';

export function UsuariosPage() {
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<string>('all');
  const [filtroActivo, setFiltroActivo] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<UsuarioAdmin | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: usuarios, isLoading } = useUsuarios({
    tipo: filtroTipo !== 'all' ? filtroTipo : undefined,
    activo: filtroActivo !== 'all' ? filtroActivo === 'true' : undefined,
    search: search || undefined,
  });

  const eliminarMutation = useEliminarUsuario();
  const resetPasswordMutation = useResetearPassword();

  const handleEdit = (usuario: UsuarioAdmin) => {
    setSelectedUsuario(usuario);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedUsuario(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await eliminarMutation.mutateAsync(deleteConfirmId);
      toast.success('Usuario eliminado correctamente');
      setDeleteConfirmId(null);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const handleResetPassword = async (id: string) => {
    try {
      const result = await resetPasswordMutation.mutateAsync(id);
      toast.success(`Contraseña restablecida: ${result.password_temporal}`);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const filteredUsuarios = usuarios?.filter((u: UsuarioAdmin) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      u.nombre.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      u.apellido?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h1>
        <Button onClick={handleCreate}>
          <UserPlus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="INTERNO">Interno</SelectItem>
              <SelectItem value="CLIENTE">Cliente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroActivo} onValueChange={setFiltroActivo}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="true">Activos</SelectItem>
              <SelectItem value="false">Inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Lista de usuarios */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : !filteredUsuarios?.length ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No se encontraron usuarios</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredUsuarios.map((usuario: UsuarioAdmin) => (
              <div key={usuario.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                    {usuario.nombre.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {usuario.nombre} {usuario.apellido}
                    </p>
                    <p className="text-sm text-gray-500">{usuario.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Badge variant={usuario.tipo === 'INTERNO' ? 'primary' : 'default'}>
                    {usuario.tipo}
                  </Badge>
                  <div className="flex gap-1">
                    {usuario.roles.map((rol) => (
                      <Badge key={rol.id} variant="info">
                        {rol.codigo}
                      </Badge>
                    ))}
                  </div>
                  <Badge variant={usuario.activo ? 'success' : 'danger'}>
                    {usuario.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                  <span className="text-sm text-gray-500 w-32">
                    {usuario.ultimo_acceso ? formatDateTime(usuario.ultimo_acceso) : '-'}
                  </span>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResetPassword(usuario.id)}
                      title="Restablecer contraseña"
                    >
                      <Key className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(usuario)}
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(usuario.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialog de formulario */}
      <UsuarioFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedUsuario(null);
        }}
        usuario={selectedUsuario}
      />

      {/* Dialog de confirmacion de eliminacion */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminacion</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mb-6">
            Esta seguro que desea eliminar este usuario? Esta accion no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={eliminarMutation.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
